import { useNavigate } from "react-router-dom";
import {
    OrderType,
    PaymentForm,
    ProductOrderItem,
    TaskSaleAdditional,
} from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { buildOfflineCheckoutOrder } from "@/pages/WorkPlan/Tasks/utils";
import {
    deductInventory,
    enqueueRequestQueueItem,
} from "@/store/slices/offline";
import { clearTaskPayload } from "@/store/slices/progress";
import { addItemToCheckout, setTaskCompleted } from "@/store/slices/work";

const buildInventoryDelta = (
    products: { barcode: string; count: number }[],
): Record<string, number> =>
    products.reduce(
        (acc, { barcode, count }) => {
            acc[barcode] = (acc[barcode] ?? 0) + count;
            return acc;
        },
        {} as Record<string, number>,
    );

interface CreateCheckoutParams {
    task: TaskSaleAdditional;
    marketId: string;
    paymentForm: PaymentForm;
    products: Record<string, ProductOrderItem>;
    shouldIgnoreRemainings: boolean;
}

export const useCreateCheckout = ({
    task,
    marketId,
    paymentForm,
    products,
    shouldIgnoreRemainings,
}: CreateCheckoutParams) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { makeTaskCompleteCommands } = useMakeCommand();

    const { markets } = useAppSelector((state) => state.work);
    const { marketsPriceLists, priceLists } = useAppSelector(
        (state) => state.offline,
    );

    const currentMarket = markets[marketId];
    const currentTask = task;

    const orderId = currentTask.context?.orderId;
    const taskId = task.id;

    const currentPriceList = priceLists?.[marketsPriceLists?.[marketId]] || {};

    const prepareOfflineCheckout = (productsArr: ProductOrderItem[]) => {
        const checkout = buildOfflineCheckoutOrder({
            productsArr,
            market: currentMarket,
            paymentForm,
            orderId,
            currentPriceList,
            orderType: OrderType.H,
        });

        dispatch(
            addItemToCheckout({
                checkoutList: {
                    [orderId]: checkout,
                },
            }),
        );
    };

    const [handleSubmit, isLoading] = useAsyncAction(async () => {
        const taskValues = products ?? {};

        const orderPayload = {
            marketProducts: { [orderId]: Object.values(taskValues) },
            paymentForms: { [orderId]: paymentForm },
            ...(shouldIgnoreRemainings && {
                ignoreRemainingsOrders: {
                    [orderId]: { ignoreRemainings: true },
                },
            }),
        };

        const productsArr = Object.values(taskValues);
        prepareOfflineCheckout(productsArr);

        const inventoryDelta = buildInventoryDelta(productsArr);
        dispatch(
            deductInventory({
                storageId: currentMarket?.storage?.id ?? "",
                newInventory: inventoryDelta,
            }),
        );

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto: false, payload: orderPayload },
            marketId,
        });

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: {
                    marketId,
                    taskId,
                    orderId,
                },
                commands: [
                    makeCommand({
                        type: "postOrderSave",
                        title: "Збереження заявки",
                        params: orderPayload,
                    }),

                    ...taskCompleteCommands,

                    makeCommand({
                        type: "getMarketInventoryGetThunk",
                        title: "Оновлення залишків",
                        params: { marketId },
                    }),
                ],
            }),
        );

        dispatch(setTaskCompleted({ marketId, taskId }));
        dispatch(clearTaskPayload({ taskId }));

        navigate(ROUTES.WorkPlan, { replace: true });
    });

    return { handle: handleSubmit, isLoading };
};
